function Policy() {
    return (
        <div>
            <h2 className="text-3xl font-bold mb-4 text-gray-800">Privacy Policy</h2>
            <p className="text-gray-700 mb-6">
                <strong>Last Updated:</strong> 17 November 2025
            </p>

            <div className="space-y-6">
                <section>
                    <h3 className="text-xl font-semibold mb-2 text-gray-800">1. Summary</h3>
                    <p className="text-gray-700">
                        Origins Bot collects the minimum amount of data necessary to provide verification, logging, and server features.
                        This Privacy Policy explains what data is collected, why it is collected, how long it is kept, and how users can remove it.
                    </p>
                    <p className="text-gray-700 mt-2">
                        By interacting with the Bot, you consent to the data handling described below.
                    </p>
                </section>

                <section>
                    <h3 className="text-xl font-semibold mb-2 text-gray-800">2. Data We Collect</h3>

                    <div className="ml-4 mt-3 space-y-4">
                        <div>
                            <h4 className="font-semibold text-gray-800 mb-2">A. Verification Data (When Users Link Accounts)</h4>
                            <p className="text-gray-700 mb-2">
                                When a user links their Discord and Habbo accounts, the Bot stores:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
                                <li>Discord User ID</li>
                                <li>Habbo Username</li>
                            </ul>
                            <p className="text-gray-700 mt-2">
                                This data is used only to confirm identity for server moderation and community integrity.
                            </p>
                        </div>

                        <div>
                            <h4 className="font-semibold text-gray-800 mb-2">B. Server/Configuration Data (For Server Owners)</h4>
                            <p className="text-gray-700 mb-2">
                                To operate logging and moderation features, the Bot stores:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
                                <li>Server (Guild) ID</li>
                                <li>Channel, forum, and thread IDs and/or names</li>
                                <li>Role IDs and/or names</li>
                                <li>Bot configuration settings</li>
                            </ul>
                            <p className="text-gray-700 mt-2">
                                This data is stored to ensure logging and moderation tools function correctly.
                            </p>
                            <p className="text-gray-700 mt-3 font-semibold">
                                The Bot does NOT store any message content, including:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
                                <li>Text messages</li>
                                <li>Edited messages</li>
                                <li>Deleted messages</li>
                                <li>Attachments</li>
                            </ul>
                            <p className="text-gray-700 mt-2">
                                Logging (such as message deletion or edits) is posted directly into the server where the event occurred.
                                No message content is saved in any database.
                            </p>
                        </div>

                        <div>
                            <h4 className="font-semibold text-gray-800 mb-2">C. Safeguarding / Anti-Impersonation Logs</h4>
                            <p className="text-gray-700 mb-2">
                                When a user unlinks their accounts and requests deletion:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
                                <li>All personal data is fully removed</li>
                                <li>A hashed, non-identifiable record is stored temporarily for security purposes</li>
                            </ul>
                            <p className="text-gray-700 mt-3 mb-2">
                                This record includes:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
                                <li>Hashed Discord ID</li>
                                <li>Hashed Habbo username</li>
                                <li>Timestamp of unlink</li>
                                <li>Reason ("user unlink")</li>
                            </ul>
                            <p className="text-gray-700 mt-3 mb-2">
                                These hashed records:
                            </p>
                            <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
                                <li>Cannot identify a person</li>
                                <li>Cannot be reversed</li>
                                <li>Are used only to prevent impersonation or fraudulent re-verification</li>
                            </ul>
                        </div>
                    </div>
                </section>

                <section>
                    <h3 className="text-xl font-semibold mb-2 text-gray-800">3. Legal Basis for Processing</h3>
                    <p className="text-gray-700 mb-2">
                        Data is collected under the following bases:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
                        <li>Legitimate Interest (verification, preventing impersonation, moderation tools)</li>
                        <li>Consent (users choose to verify; unlinking deletes their data)</li>
                        <li>Contractual necessity (Server Owners accept terms when adding the Bot)</li>
                    </ul>
                </section>

                <section>
                    <h3 className="text-xl font-semibold mb-2 text-gray-800">4. Data Retention</h3>

                    <div className="ml-4 mt-3 space-y-3">
                        <div>
                            <h4 className="font-semibold text-gray-800">Verification Data</h4>
                            <p className="text-gray-700">Stored until the user unlinks or requests deletion.</p>
                        </div>

                        <div>
                            <h4 className="font-semibold text-gray-800">Server Configuration Data</h4>
                            <p className="text-gray-700">Stored as long as the bot remains in the server. Deleted automatically when the bot is removed.</p>
                        </div>

                        <div>
                            <h4 className="font-semibold text-gray-800">Safeguarding (Hashed) Logs</h4>
                            <p className="text-gray-700">
                                Stored for up to 60 days, then automatically deleted. This is to prevent fraudulent re-verification after unlinking.
                            </p>
                        </div>
                    </div>
                </section>

                <section>
                    <h3 className="text-xl font-semibold mb-2 text-gray-800">5. Data Removal / "Forget Me" Requests</h3>
                    <p className="text-gray-700 mb-2">
                        Users may request deletion at any time by using the Bot's unlink or data removal commands. This will:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
                        <li>Delete all personal verification data</li>
                        <li>Delete association between Discord and Habbo</li>
                        <li>Trigger creation of a temporary hashed safeguarding record</li>
                        <li>Retain only non-identifiable, non-reversible hashes for 60 days</li>
                    </ul>
                    <p className="text-gray-700 mt-3">
                        Server Owners removing the Bot will automatically delete all server configuration data.
                    </p>
                </section>

                <section>
                    <h3 className="text-xl font-semibold mb-2 text-gray-800">6. Data Sharing</h3>
                    <p className="text-gray-700 mb-2">
                        The Bot does not:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
                        <li>Sell personal data</li>
                        <li>Share data with third parties</li>
                        <li>Transfer data outside the Bot's operation</li>
                    </ul>
                    <p className="text-gray-700 mt-2">
                        Your data is used only for verification and server functionality.
                    </p>
                </section>

                <section>
                    <h3 className="text-xl font-semibold mb-2 text-gray-800">7. Security</h3>
                    <p className="text-gray-700">
                        Reasonable technical measures (including hashing and access control) are used to protect stored data.
                        No system is perfectly secure, but all reasonable steps are taken to prevent unauthorised access.
                    </p>
                </section>

                <section>
                    <h3 className="text-xl font-semibold mb-2 text-gray-800">8. Children's Data</h3>
                    <p className="text-gray-700">
                        The Bot is not intended for use by anyone under 18 years old, in line with Habbo Hotel: Origins requirements.
                    </p>
                </section>

                <section>
                    <h3 className="text-xl font-semibold mb-2 text-gray-800">9. Changes to This Privacy Policy</h3>
                    <p className="text-gray-700 mb-2">
                        When the policy is updated:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 ml-4 space-y-1">
                        <li>The "Last Updated" date will be changed</li>
                        <li>Significant changes may be announced via a Bot message or support channel</li>
                        <li>Continued use of the Bot means you accept the updated policy</li>
                    </ul>
                </section>

                <section>
                    <h3 className="text-xl font-semibold mb-2 text-gray-800">10. Contact</h3>
                    <p className="text-gray-700 mb-2">
                        If you have questions or need support, contact the Bot owner via:
                    </p>
                    <ul className="list-disc list-inside text-gray-700 ml-4">
                        <li>Discord - <span className="font-mono bg-gray-100 px-2 py-1 rounded">nullpointer9681</span></li>
                    </ul>
                </section>
            </div>
        </div>
    );
}

export default Policy;